import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import {
  ArrowUpRight, BookOpen, Cable, CircleAlert, KeyRound, Pause, Play, Share2, ShieldCheck, Sparkles, UserRound, Webhook, Zap,
} from 'lucide-react';
import axios from 'src/common/axiosInstance';
import ApiKeysPage from 'src/common/apiKeys/ApiKeysPage';
import { CustomerSharingSettings } from 'src/common/customerSharing/CustomerSharing';
import { autoAppyUrl } from 'src/config/autoAppyUrls';
import WhatsAppConnectionSettings from 'src/features/WhatsAppConnectionSettings/WhatsAppConnectionSettings';
import Webhooks from 'src/features/Webhook/Webhooks';
import QuickRepliesSettings from 'src/features/QuickReplies/QuickRepliesSettings';
import { AuthContext } from 'src/context/AuthContext';
import useAiConnection, { aiStatus, aiTenant, aiUrl } from 'src/features/AiChannel/useAiConnection';
import { currentConnection } from 'src/features/Broadcasts/broadcastApi';
import { Skeleton, StagePage, Status } from 'src/stage/StageUi';
import 'src/stage/stage-settings.css';
import './settings.css';

// AUTO-WA-39: one Settings destination grouped like the main app's AUTO-319
// Settings. Every key and credential for WhatsApp now lives here:
// API keys (Developers), the Auto Appy AI key (Automation) and the webhook.
// Items with `href` open elsewhere (another page or product).
export const SETTINGS_GROUPS = [
  { id: 'whatsapp', label: 'WhatsApp', items: [
    { id: 'connection', label: 'Connection', icon: Cable, description: 'The organisation, WhatsApp Business account and number this workspace uses.' },
    { id: 'webhook', label: 'Webhook', icon: Webhook, description: 'Where Meta sends messages and delivery updates, and whether it is verified.' },
  ] },
  { id: 'chats', label: 'Chats', items: [
    { id: 'quick-replies', label: 'Quick replies', icon: Zap, description: 'Answers your team sends often, ready to insert in any chat with one tap.' },
  ] },
  { id: 'automation', label: 'Automation', items: [
    { id: 'ai', label: 'AI connection', icon: Sparkles, description: 'The Auto Appy AI key, AI replies on or off, and test mode.' },
  ] },
  { id: 'customers', label: 'Customers', items: [
    { id: 'sharing', label: 'Customer sharing', icon: Share2, description: 'Which customer records WhatsApp shares with your other Auto Appy products.' },
  ] },
  { id: 'developers', label: 'Developers', items: [
    { id: 'api-keys', label: 'API keys', icon: KeyRound, count: 'keys', description: 'Keys that let your own systems use the WhatsApp API.' },
    { id: 'api-docs', label: 'API reference', icon: BookOpen, href: '/api', hint: 'Endpoints and examples' },
  ] },
  { id: 'you', label: 'You', items: [
    { id: 'profile', label: 'Profile', icon: UserRound, href: autoAppyUrl('/profile'), hint: 'Your name, photo and sign-in, in Auto Appy' },
  ] },
];

export const SETTINGS_SECTIONS = SETTINGS_GROUPS.flatMap((group) => group.items.filter((item) => !item.href).map((item) => ({ ...item, group: group.label })));

const activeKeys = (keys = []) => keys.filter((key) => !key.revoked_at && !(key.expires_at && new Date(key.expires_at).getTime() <= Date.now()));

function useKeyCount(version) {
  const [count, setCount] = useState(null);
  useEffect(() => {
    let active = true;
    axios.get('/api-keys', { params: { application: 'whatsapp' } })
      .then((response) => { if (active) setCount(activeKeys(response.data?.data?.keys || []).length); })
      .catch(() => { if (active) setCount(null); });
    return () => { active = false; };
  }, [version]);
  return count;
}

function SettingsNav({ current, counts }) {
  return (
    <nav className="ws-card ws-set-nav" aria-label="Settings">
      {SETTINGS_GROUPS.map((group) => (
        <div key={group.id} className="ws-set-nav-group" role="group" aria-labelledby={`ws-set-group-${group.id}`}>
          <div className="ws-set-nav-label" id={`ws-set-group-${group.id}`}>{group.label}</div>
          {group.items.map((item) => {
            const Icon = item.icon;
            const count = item.count ? counts[item.count] : null;
            const content = (
              <>
                <Icon size={16} aria-hidden="true" />
                <span className="ws-grow">{item.label}</span>
                {item.href && <ArrowUpRight size={14} aria-hidden="true" className="ws-set-nav-out" />}
                {count !== null && count !== undefined && <span className="ws-set-nav-count ws-num">{count}</span>}
              </>
            );
            return item.href
              ? <a key={item.id} className="ws-set-nav-item" href={item.href} title={item.hint} target={item.href.startsWith('http') ? '_blank' : undefined} rel="noreferrer">{content}</a>
              : <Link key={item.id} className="ws-set-nav-item" to={`/settings/${item.id}`} aria-current={item.id === current ? 'page' : undefined}>{content}</Link>;
          })}
        </div>
      ))}
    </nav>
  );
}

// Settings → AI connection: the key, AI replies on/off and test mode.
// Which bots answer is chosen on the Bots page.
function AiSettings() {
  const connection = currentConnection();
  const { currentUser } = useContext(AuthContext) || {};
  const { view, loading, error, canManage, save } = useAiConnection(connection?.workspaceId);
  const [key, setKey] = useState('');
  const [testRecipient, setTestRecipient] = useState('');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);
  useEffect(() => { setTestRecipient(view.data?.test_recipient || ''); }, [view.data?.test_recipient]);
  const status = aiStatus(view);

  const run = useCallback(async (payload, message) => {
    setBusy(true); setNotice(null);
    try { await save(payload); setNotice({ tone: 'good', text: message }); setKey(''); }
    catch (failure) { setNotice({ tone: 'bad', text: failure.message }); }
    finally { setBusy(false); }
  }, [save]);

  if (loading) return <section className="ws-card ws-card-pad" style={{ display: 'grid', gap: 12 }}><Skeleton width={220} height={20} /><Skeleton height={44} /><Skeleton height={44} /></section>;
  if (error || !canManage) return <div className="wa-notice wa-notice--info" role="alert"><CircleAlert size={16} /><span>{error || 'An organisation owner or administrator manages the AI connection.'}</span></div>;
  if (!view.data) {
    return (
      <section className="ws-card ws-card-pad wa-set-empty">
        <span className="ws-icon-tile" aria-hidden="true"><Sparkles size={18} /></span>
        <div className="ws-grow"><h2 className="ws-h3">Not connected to Auto Appy AI yet</h2><p className="ws-muted">Connect your first bot on the Bots page. You’ll add the AI key there once; it then lives here.</p></div>
        <Link className="ws-btn ws-btn--primary ws-btn--sm" to="/bots">Go to Bots</Link>
      </section>
    );
  }

  const channel = { channel_id: view.data.channel_id };
  return (
    <div className="ws-set-stackcol">
      {notice && <div className={`wa-notice wa-notice--${notice.tone}`} role={notice.tone === 'bad' ? 'alert' : 'status'}><CircleAlert size={16} /><span>{notice.text}</span></div>}
      <section className="ws-card wa-set-card" aria-labelledby="ai-status">
        <div className="wa-card-head">
          <div className="ws-grow"><h2 className="ws-h3" id="ai-status">AI replies</h2><p className="ws-muted">When on, the default bot answers new WhatsApp messages.</p></div>
          <Status tone={status.tone}>{status.label}</Status>
        </div>
        <div className="wa-set-row">
          {view.data.enabled
            ? <button type="button" className="ws-btn ws-btn--quiet" disabled={busy} onClick={() => run({ ...channel, enabled: false }, 'AI replies paused. New messages stay in Chats for your team.')}><Pause size={15} />Pause AI replies</button>
            : <button type="button" className="ws-btn ws-btn--primary" disabled={busy} onClick={() => run({ ...channel, enabled: true }, 'AI replies are on.')}><Play size={15} />Turn AI replies on</button>}
          <Link className="ws-btn ws-btn--plain" to="/bots">Choose which bot answers</Link>
          <a className="ws-btn ws-btn--plain" href={aiUrl(aiTenant(view, currentUser), { channel: view.data.channel_id, tab: 'channels' })} target="_blank" rel="noreferrer">Routing and handoffs in AI<ArrowUpRight size={14} /></a>
        </div>
      </section>

      <section className="ws-card wa-set-card" aria-labelledby="ai-key">
        <div className="wa-card-head">
          <div className="ws-grow"><h2 className="ws-h3" id="ai-key">Auto Appy AI key</h2><p className="ws-muted">WhatsApp uses this key to reach your bots. It’s stored encrypted and never shown again.</p></div>
          <span className="ws-chip"><ShieldCheck size={13} aria-hidden="true" />Saved</span>
        </div>
        <form className="wa-set-row" onSubmit={(event) => { event.preventDefault(); if (key) run({ ...channel, api_key: key, enabled: view.data.enabled }, 'AI key replaced.'); }}>
          <input className="ws-input wa-set-input" type="password" autoComplete="new-password" value={key} onChange={(event) => setKey(event.target.value)} placeholder="Paste a new AI key to replace the saved one" aria-label="New Auto Appy AI key" disabled={busy} />
          <button type="submit" className="ws-btn ws-btn--primary" disabled={busy || !key}>Replace key</button>
        </form>
      </section>

      <section className="ws-card wa-set-card" aria-labelledby="ai-test">
        <div className="wa-card-head">
          <div className="ws-grow"><h2 className="ws-h3" id="ai-test">Test mode</h2><p className="ws-muted">Only this number gets AI replies and coordinated openings. Everyone else waits for your team. Clear it when you’re ready for everyone.</p></div>
          {view.data.test_recipient && <Status tone="info">On</Status>}
        </div>
        <form className="wa-set-row" onSubmit={(event) => { event.preventDefault(); run({ ...channel, enabled: view.data.enabled, test_recipient: testRecipient.trim() || null }, testRecipient.trim() ? `Test mode on for ${testRecipient.trim()}.` : 'Test mode off. AI replies to everyone.'); }}>
          <input className="ws-input wa-set-input" type="tel" value={testRecipient} onChange={(event) => setTestRecipient(event.target.value)} placeholder="Full international number, e.g. +919876543210" aria-label="Test number" disabled={busy} />
          <button type="submit" className="ws-btn ws-btn--quiet" disabled={busy || testRecipient.trim() === (view.data.test_recipient || '')}>{!testRecipient.trim() && view.data.test_recipient ? 'Turn test mode off' : 'Save test number'}</button>
        </form>
      </section>
    </div>
  );
}

export default function SettingsPage() {
  const { section } = useParams();
  const current = SETTINGS_SECTIONS.find((item) => item.id === section);
  const keys = useKeyCount(section);
  if (!current) return <Navigate to="/settings/connection" replace />;

  return (
    <StagePage className="ws-set wa-set" label="Settings">
      <header className="ws-header ws-set-header">
        <div className="ws-grow">
          <div className="ws-eyebrow">Settings · {current.group}</div>
          <h1 className="ws-title" style={{ marginTop: 6 }}>{current.label}</h1>
          {current.description && <p className="ws-muted" style={{ marginTop: 6, fontSize: 14 }}>{current.description}</p>}
        </div>
      </header>
      <div className="ws-set-layout">
        <SettingsNav current={current.id} counts={{ keys }} />
        <div className="ws-set-main">
          {current.id === 'ai' && <AiSettings />}
          {current.id === 'quick-replies' && <QuickRepliesSettings />}
          {current.id === 'connection' && <div className="wa-legacy"><WhatsAppConnectionSettings mode="settings" /></div>}
          {current.id === 'webhook' && <div className="wa-legacy"><Webhooks mode="webhook" /></div>}
          {current.id === 'sharing' && <div className="ws-embedded ws-set-embed wa-legacy wa-set-pad"><CustomerSharingSettings appName="WhatsApp" /></div>}
          {current.id === 'api-keys' && <div className="ws-embedded ws-set-embed wa-legacy"><ApiKeysPage application="whatsapp" appName="WhatsApp" endpoint="/api-keys" /></div>}
        </div>
      </div>
    </StagePage>
  );
}
